import { Component, Ref, Prop } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { BodyOverlay } from '../BodyOverlay/BodyOverlay';
import { OptionSelector } from "@/utils/OptionSelectors";
import { cart } from '@/theme/Cart';
import { formatMoney } from '@/utils/formatting';

interface QuickAddVariant {
    id: number;
    short: string | null;
    variantSize: string | null;
    imgsrc: string | null;
    option1: string | null;
    option2: string | null;
    option3: string | null;
    available: boolean;
    price: number;
}

@Component
export class ProductQuickAdd extends ThemeComponent {
    @Prop position!: number;
    @Ref({ required: false }) closeButton!: HTMLButtonElement;
    @Ref({ required: false }) submitButton!: HTMLButtonElement;
    private bodyOverlay: BodyOverlay|null = null;
    private selectedOptions: Map<number, string> = new Map();
    private valuesWithoutStock: string[] = [];
    private valuesWithoutVariant: string[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    targetVariants: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any

    init() {
        if (this.closeButton) {
            this.setCloseButtonListener();
        }

        this.bodyOverlay = document.querySelector('.body-overlay');

        if (this.bodyOverlay) {
            this.$listen(this.bodyOverlay, 'click', () => {
                this.handleButton();
            });
        }

        this.initVariantSelection();
        this.initAddToCart();
        this.initOutsideClickClose();
    }

    /**
     * Listen for close button clicks
    */
    setCloseButtonListener() {
        if (this.closeButton) {
            this.$listen(this.closeButton, 'click', () => {
                this.handleButton();
            });
        }
    }

    /**
     * Handle quick add state
    */
    handleButton() {
        this.classList.remove('is-active');
        if (this.bodyOverlay) {
            this.bodyOverlay.classList.remove('visible');
            setTimeout(() => {
                this.bodyOverlay?.classList.add('hidden');
            }, 10);
        }
        document.body.classList.remove('no-scroll');
        this.resetSelection();
    }

    private initOutsideClickClose() {
        this.$listen(window, 'click', (e: Event) => {
            if (!this.classList.contains('is-active')) {return;}
            const card = this.parentElement;
            if (card && !card.contains(e.target as Node) && (e.target as Node).isConnected) {
                this.handleButton();
            }
        });
    }

    private resetSelection() {
        const optionLists = this.querySelectorAll<HTMLUListElement>('.quick-add__options');
        for (const optionList of optionLists) {
            const optionIndex = parseInt(optionList.dataset.optionIndex ?? '0', 10);
            const items = optionList.querySelectorAll<HTMLLIElement>('.quick-add__item');
            for (const item of items) {
                item.classList.remove('is-selected');
            }
            if (items.length > 0) {
                items[0].classList.add('is-selected');
                const firstValue = items[0].dataset.value ?? items[0].textContent?.trim() ?? '';
                this.selectedOptions.set(optionIndex, firstValue);
                this.updateOptionLabel(optionIndex, firstValue);
            }
        }
        this.updateSelectedVariant(
            this.querySelector<HTMLUListElement>('.quick-add__options') as HTMLUListElement
        );
    }

    /**
     * Set up option item clicks to track selected variant
     */
    private initVariantSelection() {
        const optionLists = this.querySelectorAll<HTMLUListElement>('.quick-add__options');

        for (const optionList of optionLists) {
            const optionIndex = parseInt(optionList.dataset.optionIndex ?? '0', 10);
            const items = optionList.querySelectorAll<HTMLLIElement>('.quick-add__item');

            // Pre-select first item
            if (items.length > 0) {
                items[0].classList.add('is-selected');
                this.selectedOptions.set(optionIndex, items[0].dataset.value ?? items[0].textContent?.trim() ?? '');
            }

            for (const item of items) {
                this.$listen(item, 'click', () => {
                    for (const sibling of items) {
                        sibling.classList.remove('is-selected');
                    }
                    item.classList.add('is-selected');
                    const value = item.dataset.value ?? item.textContent?.trim() ?? '';
                    this.selectedOptions.set(optionIndex, value);
                    this.updateOptionLabel(optionIndex, value);
                    this.updateSelectedVariant(optionList);
                    this.updateOptionsImages();
                });
            }
        }

        this.updateOptionsImages();
    }

    public updateBasedOnSelector(selector: OptionSelector) {
        this.valuesWithoutStock = selector.options[this.position - 1].values
            .filter((v) => v.outOfStock).map((v) => v.value);
        this.valuesWithoutVariant = selector.options[this.position - 1].values
            .filter((v) => !v.variantExists).map((v) => v.value);
        this.targetVariants = selector.options[this.position - 1].values
            .filter((v) => v.targetVariant).map((v) => v.targetVariant);
    }

    private updateOptionsImages() {
        const optionLists = this.querySelectorAll<HTMLUListElement>('.quick-add__options');
        const optionKeys: (keyof QuickAddVariant)[] = ['option1', 'option2', 'option3'];

        for (const optionList of optionLists) {
            const variantsAttr = optionList.getAttribute('variants');
            if (!variantsAttr) { continue; }

            let variants: QuickAddVariant[];
            try {
                variants = JSON.parse(variantsAttr);
            } catch {
                continue;
            }

            const optionIndex = parseInt(optionList.dataset.optionIndex ?? '0', 10);
            const items = optionList.querySelectorAll<HTMLLIElement>('.quick-add__item--shape');
            if (items.length === 0) { continue; }

            for (const item of items) {
                const itemValue = item.dataset.value ?? '';

                const matched = variants.find((v) => {
                    for (const [idx, sel] of this.selectedOptions.entries()) {
                        const key = optionKeys[idx];
                        const compare = idx === optionIndex ? itemValue : sel;
                        if (key && v[key] !== compare) { return false; }
                    }
                    return true;
                });

                const imageContainer = item.querySelector<HTMLElement>('.quick-add__item-image');
                if (!imageContainer) { continue; }

                const existingImg = imageContainer.querySelector('img');
                if (existingImg) { existingImg.remove(); }

                if (matched?.imgsrc) {
                    const img = document.createElement('img');
                    img.src = matched.imgsrc;
                    img.alt = itemValue;
                    imageContainer.insertBefore(img, imageContainer.firstChild);
                }
            }
        }
    }

    private updateOptionLabel(optionIndex: number, value: string) {
        const selector = `.quick-add__option-name[data-option-index="${optionIndex}"] .selectedOption`;
        const label = this.querySelector<HTMLElement>(selector);
        if (label) {
            label.textContent = value;
        }
    }

    /**
     * Find the variant matching all selected options and update the hidden input.
     * Matches option1/option2/option3 by index against selectedOptions map.
     */
    private updateSelectedVariant(optionList: HTMLUListElement) {
        const variantsAttr = optionList.getAttribute('variants');
        if (!variantsAttr) {return;}

        try {
            const variants: QuickAddVariant[] = JSON.parse(variantsAttr);
            const hiddenInput = this.querySelector<HTMLInputElement>('input[type="hidden"][name="id"]');
            if (!hiddenInput) {return;}

            const optionKeys: (keyof QuickAddVariant)[] = ['option1', 'option2', 'option3'];

            const matched = variants.find((v) => {
                for (const [idx, sel] of this.selectedOptions.entries()) {
                    const key = optionKeys[idx];
                    if (key && v[key] !== sel) {return false;}
                }
                return true;
            });

            if (matched) {
                hiddenInput.value = String(matched.id);
                if (this.submitButton) {
                    this.submitButton.dataset.productid = String(matched.id);
                    const priceSpan = this.submitButton.querySelector<HTMLElement>('.price');
                    if (priceSpan) {
                        priceSpan.textContent = formatMoney(matched.price) as string;
                    }
                }
                if (matched.imgsrc) {
                    const picture = this.querySelector<HTMLPictureElement>('.quick-add__image picture');
                    if (picture) {
                        const img = picture.querySelector<HTMLImageElement>('img');
                        const source = picture.querySelector<HTMLSourceElement>('source');
                        if (img) { img.src = matched.imgsrc; }
                        if (source) { source.srcset = matched.imgsrc; }
                    }
                }
            }
        } catch {
            this.$log.error('Failed to parse variants JSON');
        }
    }

    /**
     * Listen for submit button click and add to cart via cart API, same as ShopTheLook
     */
    private initAddToCart() {
        if (!this.submitButton) {return;}

        this.$listen(this.submitButton, 'click', async(e) => {
            e.preventDefault();

            const hiddenInput = this.querySelector<HTMLInputElement>('input[type="hidden"][name="id"]');
            if (!hiddenInput) {return;}

            const variantId = parseInt(hiddenInput.value, 10);
            if (!variantId) {return;}

            await cart.addItems([{ id: variantId, quantity: 1 }])
                .then(() => {
                    document.dispatchEvent(new Event('cartChanged'));
                    this.handleButton();
                })
                .catch((error: unknown) => {
                    this.$log.error('Cart error', { error });
                });
        });
    }

}
